/**
 * Catálogo de módulos da Home.
 *
 * tipo:   kpi | barras | comparativo | evolucao | lista | atalhos
 * destaque: card com fundo verde — usado para as reuniões REALIZADAS, o indicador principal
 * dados:  de onde o módulo lê (resumo = GET dashboard/resumo; os demais são listas próprias)
 * visoes: em quais visões o módulo pode ser adicionado
 * O id precisa existir também em DashboardController::MODULOS (validação do layout salvo).
 */

const fmt = (n) => Number(n || 0).toLocaleString('pt-BR');
const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) + '%' : '—');
const variacao = (atual, anterior) => {
    if (!anterior) return { texto: '—', tom: 'neutro' };
    const v = Math.round(((atual - anterior) / anterior) * 100);
    return { texto: (v > 0 ? '+' : '') + v + '%', tom: v >= 0 ? 'bom' : 'ruim' };
};

export const MODULOS = {
    kpi_realizadas: {
        titulo: 'Reuniões realizadas', tipo: 'kpi', dados: 'resumo', visoes: ['gestao'], destaque: true,
        kpi: (k) => {
            const v = variacao(k.realizadas_periodo, k.realizadas_anterior);
            return { valor: fmt(k.realizadas_periodo), destaque: v.texto, tom: v.tom, rodape: 'vs. período anterior' };
        },
    },
    kpi_minhas_realizadas: {
        titulo: 'Minhas reuniões realizadas', tipo: 'kpi', dados: 'resumo', visoes: ['assessor'], destaque: true,
        kpi: (k) => {
            const v = variacao(k.realizadas_periodo, k.realizadas_anterior);
            return { valor: fmt(k.realizadas_periodo), destaque: v.texto, tom: v.tom, rodape: 'vs. período anterior' };
        },
    },
    kpi_leads: {
        titulo: 'Leads na base', tipo: 'kpi', dados: 'resumo', visoes: ['gestao'],
        kpi: (k) => ({ valor: fmt(k.leads_base), destaque: '+' + fmt(k.leads_importados), tom: 'bom', rodape: 'importados no período' }),
    },
    kpi_reunioes: {
        titulo: 'Reuniões agendadas', tipo: 'kpi', dados: 'resumo', visoes: ['gestao'],
        kpi: (k) => {
            const v = variacao(k.reunioes, k.reunioes_anterior);
            return { valor: fmt(k.reunioes), destaque: v.texto, tom: v.tom, rodape: 'vs. período anterior' };
        },
    },
    kpi_realizacao: {
        titulo: 'Taxa de realização', tipo: 'kpi', dados: 'resumo', visoes: ['gestao', 'assessor'],
        kpi: (k) => ({ valor: pct(k.realizadas, k.realizacao_total), destaque: fmt(k.reagendadas), tom: k.reagendadas > 0 ? 'ruim' : 'neutro', rodape: 'reagendada(s) no período' }),
    },
    kpi_auditoria: {
        titulo: 'Aguardando auditoria', tipo: 'kpi', dados: 'resumo', visoes: ['gestao'],
        kpi: (k) => ({ valor: fmt(k.auditoria_pendente), destaque: fmt(k.auditoria_atrasada), tom: k.auditoria_atrasada > 0 ? 'ruim' : 'neutro', rodape: 'há mais de 48 h' }),
    },
    kpi_briefing: {
        titulo: 'Briefings pendentes', tipo: 'kpi', dados: 'resumo', visoes: ['gestao', 'assessor'],
        kpi: (k) => ({ valor: fmt(k.briefing_pendente), destaque: fmt(k.briefing_urgente), tom: k.briefing_urgente > 0 ? 'alerta' : 'neutro', rodape: 'com reunião nos próximos 2 dias' }),
    },
    kpi_followup: {
        titulo: 'Follow-ups de hoje', tipo: 'kpi', dados: 'resumo', visoes: ['gestao', 'assessor'],
        kpi: (k) => ({ valor: fmt(k.followups_hoje), destaque: fmt(k.followups_atrasados), tom: k.followups_atrasados > 0 ? 'ruim' : 'neutro', rodape: 'atrasado(s)' }),
    },
    kpi_meus_leads: {
        titulo: 'Leads em andamento', tipo: 'kpi', dados: 'resumo', visoes: ['gestao', 'assessor'],
        kpi: (k) => ({ valor: fmt(k.leads_em_andamento), destaque: fmt(k.leads_aguardando), tom: 'neutro', rodape: 'aguardando retorno' }),
    },
    kpi_minhas_reunioes: {
        titulo: 'Minhas reuniões agendadas', tipo: 'kpi', dados: 'resumo', visoes: ['assessor'],
        kpi: (k) => {
            const v = variacao(k.reunioes, k.reunioes_anterior);
            return { valor: fmt(k.reunioes), destaque: v.texto, tom: v.tom, rodape: 'vs. período anterior' };
        },
    },
    kpi_clientes: {
        titulo: 'Clientes ativos', tipo: 'kpi', dados: 'resumo', visoes: ['gestao'],
        kpi: (k) => ({ valor: fmt(k.clientes_ativos), destaque: fmt(k.mailings), tom: 'neutro', rodape: 'mailings vinculados' }),
    },

    pipeline: {
        titulo: 'Pipeline de leads (quadro)', tipo: 'barras', dados: 'resumo', visoes: ['gestao', 'assessor'],
        link: { texto: 'Abrir quadro', to: '/gestao-assessor' },
        barras: (r) => r.pipeline.map((p) => ({ rotulo: p.rotulo, total: p.total, cor: corPipeline(p.status) })),
    },
    status_reunioes: {
        titulo: 'Situação das agendadas', tipo: 'barras', dados: 'resumo', visoes: ['gestao', 'assessor'],
        link: { texto: 'Ver reuniões', to: '/agendamento-lead' },
        barras: (r) => r.status_reunioes.map((s) => ({ rotulo: s.rotulo, total: s.total, cor: corStatusReuniao(s.status) })),
    },
    evolucao: {
        titulo: 'Realizadas × agendadas', tipo: 'evolucao', dados: 'resumo', visoes: ['gestao', 'assessor'],
        link: { texto: 'Ver reuniões', to: '/agendamento-lead' },
    },
    por_assessor: {
        titulo: 'Reuniões por assessor', tipo: 'comparativo', dados: 'resumo', visoes: ['gestao'],
        link: { texto: 'Ver reuniões', to: '/agendamento-lead' },
        linhas: (r) => r.por_assessor,
    },
    por_cliente: {
        titulo: 'Reuniões por projeto', tipo: 'comparativo', dados: 'resumo', visoes: ['gestao'],
        link: { texto: 'Ver reuniões', to: '/agendamento-lead' },
        linhas: (r) => r.por_cliente,
    },

    proximas_reunioes: {
        titulo: 'Próximas reuniões', tipo: 'lista', dados: 'proximas-reunioes', visoes: ['gestao', 'assessor'],
        link: { texto: 'Ver todas', to: '/agendamento-lead' },
    },
    followups: {
        titulo: 'Follow-ups vencidos e de hoje', tipo: 'lista', dados: 'followups', visoes: ['gestao', 'assessor'],
        link: { texto: 'Ver meus leads', to: '/meus-leads' },
    },
    auditoria: {
        titulo: 'Fila de auditoria', tipo: 'lista', dados: 'auditoria', visoes: ['gestao'],
        link: { texto: 'Ver reuniões', to: '/agendamento-lead' },
    },
    importacoes: {
        titulo: 'Importações recentes', tipo: 'lista', dados: 'importacoes', visoes: ['gestao'],
        link: { texto: 'Ver logs', to: '/leads/importacao' },
    },
    atalhos: {
        titulo: 'Ações rápidas', tipo: 'atalhos', dados: null, visoes: ['gestao', 'assessor'],
    },
};

/** Layout padrão de cada visão: [id, largura em colunas de 12]. */
export const PADROES = {
    gestao: [
        ['kpi_realizadas', 3], ['kpi_reunioes', 3], ['kpi_realizacao', 3], ['kpi_auditoria', 3],
        ['evolucao', 12],
        ['por_assessor', 6], ['por_cliente', 6],
        ['proximas_reunioes', 8], ['atalhos', 4],
        ['pipeline', 6], ['status_reunioes', 6],
        ['auditoria', 6], ['importacoes', 6],
    ],
    assessor: [
        ['kpi_minhas_realizadas', 3], ['kpi_minhas_reunioes', 3], ['kpi_followup', 3], ['kpi_briefing', 3],
        ['followups', 6], ['atalhos', 6],
        ['evolucao', 12],
        ['proximas_reunioes', 12],
        ['pipeline', 6], ['status_reunioes', 6],
    ],
};

export const LARGURAS = [3, 4, 6, 8, 12];
export const ROTULO_LARGURA = { 3: '¼', 4: '⅓', 6: '½', 8: '⅔', 12: '1/1' };

export const layoutPadrao = (visao) => PADROES[visao].map(([id, span]) => ({ id, span }));

/** Descarta módulos desconhecidos ou não permitidos na visão (ex.: layout salvo antigo). */
export const sanearLayout = (layout, visao) =>
    (layout || []).filter((m) => MODULOS[m.id] && MODULOS[m.id].visoes.includes(visao) && LARGURAS.includes(m.span));

/** Atalhos filtrados pela ação do perfil. */
export const ATALHOS = [
    { rotulo: 'Novo lead', to: '/leads/novo', acao: 'editar_leads' },
    { rotulo: 'Agendar reunião', to: '/agendamento-lead/novo', acao: 'editar_reunioes' },
    { rotulo: 'Abrir quadro', to: '/gestao-assessor', acao: 'visualizar' },
    { rotulo: 'Meus leads de hoje', to: '/meus-leads', acao: 'visualizar' },
    { rotulo: 'Importar leads', to: '/leads/importacao', acao: 'importar', somenteGestao: true },
    { rotulo: 'Novo cliente', to: '/customer/new', acao: 'editar_clientes', somenteGestao: true },
    { rotulo: 'Novo mailing', to: '/mailing/new', acao: 'editar_clientes', somenteGestao: true },
    { rotulo: 'Gerar relatório', to: '/relatorio', acao: 'gerar_relatorios', somenteGestao: true },
    { rotulo: 'Novo usuário', to: '/user/new', acao: 'gerenciar_usuarios', somenteGestao: true },
];

function corPipeline(status) {
    if (status === 7) return '#2F6420';
    if (status === 6) return '#A63A1A';
    if (status === 0 || status === 4 || status === 5) return '#9AAE90';
    return '#4B8B33';
}

function corStatusReuniao(status) {
    switch (status) {
        case 'realizada': return '#2F6420';
        case 'confirmado': return '#3B6FD8';
        case 'aguardando':
        case 'reagendada': return '#C98A1E';
        case 'declinada':
        case 'nao_aprovada': return '#A63A1A';
        default: return '#9AAE90';
    }
}
